namespace Solovis.Reports.Parameters.PropValueIncludes {


    export class PropValuesToIncludeSetEditorDialog {

        // "Manager" is a shortcut for "Another Property's Property Value" with this system property
        // (a ManagerRef property defined on Entity resources) pre-selected as the reference. It is
        // collapsed to a plain "manager" resourceRef while the editor is open, and expanded back to
        // the equivalent indirect reference on save, so existing indirect-resolution logic (both here
        // and on the backend) handles it with no further changes. Only reports whose "manager"
        // resourceOption declares an indirectRefCode opt into this - a report whose line items are
        // already Managers (e.g. Manager List) has no indirectRefCode, so "manager" is left as the
        // direct resourceRef it already resolves natively.
        //
        // This stays needed here even though PropValuesToIncludeOption.ExpandManagerShortcutsAsync
        // does the same expansion server-side: that backend path only runs for the new report
        // builder pipeline. Every report using this control (Interval Stats and the other
        // App_ReportJs reports) resolves resourceRef entirely client-side in
        // PropValuesToIncludeHelper.ts and never reaches that backend code, so the shortcut still
        // has to be expanded before it leaves the browser.
        private static readonly ManagerReferencePropertyCode = "Sys.Manager";

        private _portalServices: Solovis.Portal.IPortalServices;

        private _resourceOptions: PropValueToIncludeResourceOption[];
        private _dateOptions: PropValueToIncludeDateOption[];

        private _setOptions: PropValuesToIncludeSetOptions;
        private _saveCallback;

        private _formBuilder: Solovis.UI.InputControls.FormBuilder;
      private _dialog: Solovis.UI.Dialog;
      private container: HTMLElement;

        private _propDefs: InvData.Models.IdAndCodeObjectIndex<InvData.PropertyDefinition>;

        private _codeToResourceOptionMap = {};


        public constructor(options) {
            if (options.portalServices) {
                this._portalServices = options.portalServices;
            }
            else {
                this._portalServices = new Solovis.Portal.SitePortalServices();
            }

            this.container = options.container;
            this._setOptions = options.setOptions ? options.setOptions : {};
            this._resourceOptions = options.resourceOptions;
            this._dateOptions = options.dateOptions;
            this._saveCallback = options.saveCallback;

            for (let resourceOption of this._resourceOptions) {
                this._codeToResourceOptionMap[resourceOption.code] = resourceOption;
            }
        }

        public async show() {
            this._propDefs = await this._portalServices.modelEnvironmentCachedData().getPropertyDefinitionsAsync();

            if (this._setOptions.includeList) {
                this._setOptions.includeList = this._setOptions.includeList.map(
                    (propValueToInclude) => PropValuesToIncludeSetEditorDialog.collapseManagerShortcut(propValueToInclude, this._propDefs, this._resourceOptions));
            }

          if (!Boolean((Solovis as any).Info.userEligibleForPropertyValuesToIncludeRedesign)) {
            this._internalShow();
          } else {
            const self = this;
            const selectedList = this._setOptions.showFromDate ? this._setOptions.includeList.map((prop) => { return { ...prop, showFromDate: this._setOptions.showFromDate } }) : this._setOptions.includeList;
            Solovis.UX.Utils.renderModal("PropertyValuesToIncludeEditor", {
              // Save values
              onSubmit: (newOptions) => {
                const expandedOptions = (newOptions || []).map(
                  (propValueToInclude) => PropValuesToIncludeSetEditorDialog.expandManagerShortcut(propValueToInclude, this._propDefs, this._resourceOptions));
                this._saveCallback({ includeList: expandedOptions, showFromDate: undefined });
              },
              selected: selectedList || [],
              dateOptions: this._dateOptions,
              resourceOptions: this._resourceOptions
            }, self.container);
          } 
        }

        private static getManagerIndirectRefCode(resourceOptions: PropValueToIncludeResourceOption[]): string | undefined {
            return resourceOptions?.find((option) => option.code === "manager")?.indirectRefCode;
        }

        private static collapseManagerShortcut(
            propValueToInclude: PropValueToInclude,
            propDefs: InvData.Models.IdAndCodeObjectIndex<InvData.PropertyDefinition>,
            resourceOptions: PropValueToIncludeResourceOption[]
        ): PropValueToInclude {
            if (!propValueToInclude) {
                return propValueToInclude;
            }

            const indirectRefCode = PropValuesToIncludeSetEditorDialog.getManagerIndirectRefCode(resourceOptions);
            const indirectRef = propValueToInclude.indirectRef;
            const managerPropDef = (indirectRefCode && propDefs) ? propDefs.tryGetByCode(PropValuesToIncludeSetEditorDialog.ManagerReferencePropertyCode) : null;

            if (indirectRefCode && propValueToInclude.resourceRef === "indirect" && indirectRef && !indirectRef.indirectRef
                && indirectRef.resourceRef === indirectRefCode
                && managerPropDef && indirectRef.propDefId === managerPropDef.id) {
                return { ...propValueToInclude, resourceRef: "manager", indirectRef: undefined };
            }

            if (indirectRef) {
                return { ...propValueToInclude, indirectRef: PropValuesToIncludeSetEditorDialog.collapseManagerShortcut(indirectRef, propDefs, resourceOptions) };
            }

            return propValueToInclude;
        }

        private static expandManagerShortcut(
            propValueToInclude: PropValueToInclude,
            propDefs: InvData.Models.IdAndCodeObjectIndex<InvData.PropertyDefinition>,
            resourceOptions: PropValueToIncludeResourceOption[]
        ): PropValueToInclude {
            if (!propValueToInclude) {
                return propValueToInclude;
            }

            if (propValueToInclude.resourceRef === "manager") {
                const indirectRefCode = PropValuesToIncludeSetEditorDialog.getManagerIndirectRefCode(resourceOptions);
                if (!indirectRefCode) {
                    return propValueToInclude;
                }

                const managerPropDef = propDefs ? propDefs.tryGetByCode(PropValuesToIncludeSetEditorDialog.ManagerReferencePropertyCode) : null;
                if (!managerPropDef) {
                    return propValueToInclude;
                }

                return {
                    ...propValueToInclude,
                    resourceRef: "indirect",
                    indirectRef: {
                        resourceRef: indirectRefCode,
                        propDefId: managerPropDef.id,
                        dateRef: propValueToInclude.dateRef,
                        exactDayOnly: propValueToInclude.exactDayOnly
                    }
                };
            }

            if (propValueToInclude.indirectRef) {
                return {
                    ...propValueToInclude,
                    indirectRef: PropValuesToIncludeSetEditorDialog.expandManagerShortcut(propValueToInclude.indirectRef, propDefs, resourceOptions)
                };
            }

            return propValueToInclude;
        }

        public static getPropValueToIncludeLabel(
            propValueToInclude: PropValueToInclude,
            propDefs: InvData.Models.IdAndCodeObjectIndex<InvData.PropertyDefinition>,
            codeToResourceOptionMap
        ): string {

            let propDefName = "(Property)";
            let resourceRefName = "(Resource)";
            let dateRefName = null;

            if (propValueToInclude) {
                if (propValueToInclude.propDefId) {
                    let propDef = propDefs.tryGetById(propValueToInclude.propDefId);
                    if (propDef) {
                        propDefName = propDef.label;
                    }
                }

                if (propValueToInclude.resourceRef) {
                    if (propValueToInclude.resourceRef === "indirect") {
                        let indirectLabel = this.getPropValueToIncludeLabel(propValueToInclude.indirectRef, propDefs, codeToResourceOptionMap);
                        resourceRefName = "(" + indirectLabel + ")";
                    }
                    else {
                        let resourceOption = codeToResourceOptionMap[propValueToInclude.resourceRef];
                        if (resourceOption) {
                            resourceRefName = resourceOption.label;
                        }
                    }
                }
            }

            let descriptionText = propDefName + " For " + resourceRefName;
            if (dateRefName) {
                descriptionText += " At " + dateRefName;
            }

            if (propValueToInclude && propValueToInclude.exactDayOnly) {
                descriptionText += " (Exact Day Only)";
            }

            return descriptionText;
        }

        private _internalShow() {
            let setOptions = this._setOptions ? this._setOptions : {};

            let formBuilderDiv = $("<div>");
            let formBuilder = new Solovis.UI.InputControls.FormBuilder({
                container: formBuilderDiv,
                width: 800,
                labelColumnWidth: 100,
                portalServices: this._portalServices
            });
            this._formBuilder = formBuilder;

            formBuilder.addEntry({
                name: "includeList",
                label: "Include",

                initialValue: setOptions.includeList,

                applyStandardTextAlignTopPadding: true,

                createEditor: (createEditorOptions) => {
                    let groupListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
                        controlHost: createEditorOptions.container,

                        itemTypeLabel: "Property",
                        showEditLinks: true,

                        values: createEditorOptions.initialValue,

                        editorCreator: (editorDiv, initialValue) => {
                            let lineItemState = {
                                propValueToInclude: initialValue
                            };

                            let updateEditorDiv = () => {
                                let descriptionText = PropValuesToIncludeSetEditorDialog.getPropValueToIncludeLabel(lineItemState.propValueToInclude, this._propDefs, this._codeToResourceOptionMap);

                                editorDiv.text(descriptionText);
                            };

                            updateEditorDiv();

                            let editorInfo = {
                                getValue: () => {
                                    return lineItemState.propValueToInclude;
                                },

                                showEditor: () => {
                                    PropValueToIncludeControl.showEditDialog(
                                        {
                                            resourceOptions: this._resourceOptions,
                                            dateOptions: this._dateOptions,
                                            propValueToInclude: lineItemState.propValueToInclude,
                                            propDefs: this._propDefs,
                                            codeToResourceOptionMap: this._codeToResourceOptionMap,
                                            portalServices: this._portalServices
                                        },
                                        (newPropValueToInclude) => {
                                            lineItemState.propValueToInclude = newPropValueToInclude;
                                            updateEditorDiv();
                                        });
                                }
                            };

                            return editorInfo;
                        },

                        newValueGenerator: () => {
                            let newPropValueToInclude = {};

                            return newPropValueToInclude;
                        }
                    });
                    groupListBuilder.setAsCurrentControl();

                    let editor = {
                        getValue: () => {
                            return groupListBuilder.getValues();
                        }
                    };

                    return editor;
                }
            });

            formBuilder.addEntry({
                name: "showFromDate",
                label: "Show Since Date",
                initialValue: !!setOptions.showFromDate,

                editor: {
                    type: "bool"
                }
            });

            formBuilder.setupDone();

            formBuilder.showAsDialog({
                title: "Edit Property Values To Include Set",

                desiredWidth: 800,
                desiredHeight: 600,

                showSaveCancel: true,
                saveButtonText: "OK",

                saveCallback: () => {
                    let newOptions = this._getSetOptions();

                    this._saveCallback(newOptions);

                    return true;
                }
            });
        }

        private _getSetOptions() {
            let newPropValuesToIncludeSetOptions: PropValuesToIncludeSetOptions = {};

            const includeList = this._formBuilder.getValueByName("includeList");
            newPropValuesToIncludeSetOptions.includeList = includeList
                ? includeList.map((propValueToInclude) => PropValuesToIncludeSetEditorDialog.expandManagerShortcut(propValueToInclude, this._propDefs, this._resourceOptions))
                : includeList;

            if (this._formBuilder.getValueByName("showFromDate")) {
                newPropValuesToIncludeSetOptions.showFromDate = true;
            }

            if (!_.isEmpty(newPropValuesToIncludeSetOptions.includeList)) {
                return newPropValuesToIncludeSetOptions;
            }
            else {
                return null;
            }
        }
    }


}